import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams, useNavigate, Link } from "react-router-dom";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer";
import AddImage2 from "../assets/img/about.png";
import NavaService from "../services/NavaService";
import SEO from "../components/SEO";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    NavaService.getPropertyById(id)
      .then((res) => {
        setProperty(res.data);
        const first = res.data?.images?.[0];
        setActiveImage(first ? NavaService.getPropertyImageUrl(id, first.file_name) : null);
      })
      .catch(() => setError("This property could not be found."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <>
        <TopNavbar />
        <Wrapper><div className="container"><StatusText>Loading property...</StatusText></div></Wrapper>
        <Footer />
      </>
    );
  }

  if (error || !property) {
    return (
      <>
        <TopNavbar />
        <Wrapper>
          <div className="container">
            <StatusText>{error || "Property not found."}</StatusText>
            <div className="flexCenter"><BackLink to="/properties">← Back to all properties</BackLink></div>
          </div>
        </Wrapper>
        <Footer />
      </>
    );
  }

  const images = property.images || [];

  return (
    <>
      <SEO
        title={`${property.title_number} in ${property.location}`}
        description={`View verified auction details for title ${property.title_number} in ${property.location}, ${property.county_name}.`}
        image={activeImage || "/sare-logo.png"}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "RealEstateListing",
          name: property.title_number,
          description: property.description || `Property available for auction in ${property.location}.`,
          url: typeof window !== "undefined" ? window.location.href : "/",
          image: activeImage || undefined,
        }}
      />
      <TopNavbar />
      <Wrapper>
        <div className="container">
          <BackLink to="/properties">← Back to all properties</BackLink>

          <TitleRow className="flexSpaceCenter">
            <div>
              <h1 className="font40 extraBold" style={{ margin: 0 }}>{property.title_number}</h1>
              <p className="font13" style={{ color: "#707070", margin: "6px 0 0" }}>
                {property.location} · {property.county_name}
              </p>
            </div>
            <StatusPill status={property.status}>{property.status.replace("_", " ")}</StatusPill>
          </TitleRow>

          <Layout>
            <Gallery>
              <MainImage src={activeImage || AddImage2} alt={property.title_number} className="radius8" />
              {images.length > 1 && (
                <Thumbs>
                  {images.map((img) => {
                    const url = NavaService.getPropertyImageUrl(property.id, img.file_name);
                    return (
                      <Thumb
                        key={img.id}
                        src={url}
                        active={url === activeImage}
                        onClick={() => setActiveImage(url)}
                        className="pointer radius6"
                        alt=""
                      />
                    );
                  })}
                </Thumbs>
              )}

              <ContactCta>
                <p className="font13">Interested in this property?</p>
                <CtaBtn
                  onClick={() => navigate("/", {
                    state: {
                      scrollTo: "contact",
                      contactSubject: `Inquiry about title No ${property.title_number}`,
                    },
                  })}
                  className="pointer radius8"
                >
                  Contact us about this listing
                </CtaBtn>
              </ContactCta>
            </Gallery>

            <Details>
              <FactGrid>
                {property.tenure && <Fact label="Tenure" value={property.tenure} />}
                {property.approximate_area && <Fact label="Approximate area" value={property.approximate_area} />}
                {property.user && <Fact label="User" value={property.user} />}
                {property.improvements && <Fact label="Improvements" value={property.improvements} />}
                {property.services && <Fact label="Services" value={property.services} />}
                {(property.latitude && property.longitude) && (
                  <Fact label="Coordinates" value={`${property.latitude}, ${property.longitude}`} />
                )}
                {property.coordinates_text && <Fact label="Surveyed coordinates" value={property.coordinates_text} />}
              </FactGrid>

              {property.situation && <Section title="Situation" text={property.situation} />}
              {property.description && <Section title="Description" text={property.description} />}
            </Details>
          </Layout>
        </div>
      </Wrapper>
      <Footer />
    </>
  );
}

function Fact({ label, value }) {
  return (
    <FactItem>
      <span className="font13">{label}</span>
      <strong>{value}</strong>
    </FactItem>
  );
}

function Section({ title, text }) {
  return (
    <SectionBlock>
      <h4>{title}</h4>
      <p>{text}</p>
    </SectionBlock>
  );
}

const Wrapper = styled.section`
  width: 100%;
  padding-top: 120px;
  padding-bottom: 80px;
  color: #5d191a;
`;

const StatusText = styled.p`
  text-align: center;
  color: #707070;
  padding: 60px 0;
`;

const BackLink = styled(Link)`
  display: inline-block;
  color: #5d191a;
  font-weight: 700;
  font-size: 13px;
  margin-bottom: 20px;
  text-decoration: none;
`;

const TitleRow = styled.div`
  margin-bottom: 26px;
  flex-wrap: wrap;
  gap: 12px;
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  text-transform: capitalize;
  background: ${(p) =>
    p.status === "sold" ? "#fdecec" :
    p.status === "withdrawn" ? "#f0f0f0" :
    p.status === "under_auction" ? "#fff4e0" : "#e9f9ee"};
  color: ${(p) =>
    p.status === "sold" ? "#d63b3b" :
    p.status === "withdrawn" ? "#707070" :
    p.status === "under_auction" ? "#b76e00" : "#1a8f4c"};
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Gallery = styled.div``;

const MainImage = styled.img`
  width: 100%;
  height: 360px;
  object-fit: cover;
  display: block;
`;

const Thumbs = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const Thumb = styled.img`
  width: 70px;
  height: 55px;
  object-fit: cover;
  opacity: ${(p) => (p.active ? 1 : 0.6)};
  border: 2px solid ${(p) => (p.active ? "#5d191a" : "transparent")};
`;

const Details = styled.div``;

const FactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 26px;

  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const FactItem = styled.div`
  display: flex;
  flex-direction: column;
  span { color: #707070; font-size: 12px; margin-bottom: 4px; }
  strong { color: #3b1718; }
`;

const SectionBlock = styled.div`
  margin-bottom: 22px;

  & + & {
    border-top: 1px solid #eadfe0;
    padding-top: 18px;
  }

  h4 {
    margin: 0 0 6px;
    color: #3b1718;
    font-size: 15px;
  }
  p {
    margin: 0;
    color: #444;
    line-height: 1.6;
    white-space: pre-wrap;
  }
`;

const ContactCta = styled.div`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eadfe0;

  p { color: #5d191a; margin: 0 0 10px; }
`;

const CtaBtn = styled.button`
  border: 0;
  background: #5d191a;
  color: #fff;
  padding: 12px 24px;
  font-weight: 700;
  &:hover { background: #421214; }
`;
